import { Box, Button, Dialog, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { LogoTransPreto } from 'assets/icons';

import styles from './styles';

const STORAGE_KEY = 'clube42-age-verified';
const SKIP_PATHS = ['/descadastro'];

type AgeStatus = 'pending' | 'verified' | 'blocked';

const readStoredStatus = (): AgeStatus => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1' ? 'verified' : 'pending';
  } catch {
    return 'pending';
  }
};

interface AgeVerificationGateProps {
  children: React.ReactNode;
}

const AgeVerificationGate: React.FC<AgeVerificationGateProps> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const [status, setStatus] = useState<AgeStatus>(readStoredStatus);
  const skipGate = SKIP_PATHS.some(path => pathname.startsWith(path));

  const handleConfirm = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Ignore storage errors and still allow access in this session.
    }
    setStatus('verified');
  }, []);

  const handleDecline = useCallback(() => {
    setStatus('blocked');
  }, []);

  const isOpen = !skipGate && status !== 'verified';

  return (
    <>
      {children}
      <Dialog
        open={isOpen}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        sx={styles.dialog}
        slotProps={{
          backdrop: { sx: styles.backdrop },
          paper: { sx: styles.paper },
        }}
      >
        <Box
          component="img"
          src={LogoTransPreto}
          alt="Clube da 42"
          sx={styles.logo}
        />
        {status === 'blocked' ? (
          <>
            <Typography sx={styles.title}>
              Este site é exclusivo para maiores de 18 anos.
            </Typography>
            <Typography sx={styles.subtitle}>
              A comercialização de bebidas alcoólicas é destinada somente a
              pessoas com 18 anos ou mais.
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={styles.title}>
              Ops... antes de continuar, precisamos saber se você é maior de
              idade.
            </Typography>
            <Typography sx={styles.subtitle}>
              Somente pessoas com <strong>18 anos</strong> ou acima podem
              utilizar este site.
            </Typography>
            <Box sx={styles.actions}>
              <Button
                variant="outlined"
                onClick={handleDecline}
                sx={styles.outlinedButton}
              >
                Não sou maior de idade
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                sx={styles.confirmButton}
              >
                Sim, sou maior de idade
              </Button>
            </Box>
          </>
        )}
      </Dialog>
    </>
  );
};

export default AgeVerificationGate;
